import React, { ChangeEvent, useCallback, useState, VFC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { capitalizeFirstLetter } from '@dxb-web-ui-framework/common/utils/String';
import { isDefined, isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { Box, CardHeading, Pagination, Paper } from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { renderTableGridActions, TableGrid } from '../../../../content-composer/src/components/Generic/TableGrid';
import { AccountExtraitTransaction } from '../../domain/Transaction/entities/AccountExtraitTransaction';
import { ConsolidatedTransactionDetails } from '../../domain/Transaction/entities/ConsolidatedTransaction';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import { TransactionDetailsDrawer } from '../TransactionDetails/TransactionDetailsDrawer';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { SelectedFiltersChip } from '../../../../content-composer/src/components/Generic/SelectedFiltersChip';
import { TableFilterDrawer } from '../../../../content-composer/src/components/Generic/TableGrid';
import { AccountExtraitTransactionListFilters } from '../../../../account/src/contexts/AccountDetails/types';
import {
  useAccountExtraitTransactionsProviderLoadingState,
  useAccountExtraitTransactionsProviderErrorState,
  useResetSelectedAccountExtraitTransaction,
  useAccountExtraitTransactionDetails,
  useSelectedAccountExtraitTransaction,
  useCheckAuthorization,
} from '../../contexts/AccountExtraitTransactions/hooks';
import { useAccountExtraitTransactionsGridConfig } from './useAccountExtraitTransactionsGridConfig';
import {
  TRANSACTION_TYPE_DEFAULT_VALUE,
  useAccountExtraitTransactionsFiltersConfig,
} from './useAccountExtraitTransactionsFiltersConfig';

export type TransactionsTableByAccountNumberProps = {
  transactions: Nullable<AccountExtraitTransaction[]>;
  customerId: string;
  transactionListError: any;
  filters: Partial<AccountExtraitTransactionListFilters>;
  onChangeFilters: (newFilters: Partial<AccountExtraitTransactionListFilters>) => void;
  isLoadingTransactionList: boolean;
  isTransactionsFilterOpen: boolean;
  onSelectTransaction: (transactionId?: string) => void;
  onToggleFilterDrawer: () => void;
  selectedAccountId?: number;
  totalItems?: number;
  currentPage: number;
  onPageChange?: (event: ChangeEvent<unknown>, nextPage: number) => void;
  selectedAccountCurrency?: string;
};

const AccountExtraitTransactionsTable: VFC<TransactionsTableByAccountNumberProps> = ({
  transactions,
  customerId,
  transactionListError,
  filters,
  onChangeFilters,
  isLoadingTransactionList,
  isTransactionsFilterOpen,
  onSelectTransaction,
  selectedAccountId,
  totalItems,
  currentPage,
  onPageChange,
  onToggleFilterDrawer,
  selectedAccountCurrency,
}) => {
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(false);

  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const selectedTransactionDetails = useAccountExtraitTransactionDetails();
  const selectedTransaction = useSelectedAccountExtraitTransaction();
  const checkAuthorization = useCheckAuthorization();
  const isLoadingTransactionDetails = useAccountExtraitTransactionsProviderLoadingState(
    'isLoadingAccountExtraitTransactionDetails',
  );
  const isLoadingCheckAuthorization = useAccountExtraitTransactionsProviderLoadingState('isLoadingCheckAuthorization');
  const transactionDetailsError = useAccountExtraitTransactionsProviderErrorState(
    'accountExtraitTransactionDetailsError',
  );
  const navigate = useNavigate();
  const { t } = useTranslation();

  const resetSelectedAccountExtraitTransaction = useResetSelectedAccountExtraitTransaction();

  const onToggleDrawer = useCallback(
    (transactionId: string) => {
      if (!isTransactionDetailsOpen) {
        onSelectTransaction(transactionId);
      } else {
        resetSelectedAccountExtraitTransaction();
      }
      setIsTransactionDetailsOpen(prev => !prev);
    },
    [onSelectTransaction, isTransactionDetailsOpen, resetSelectedAccountExtraitTransaction],
  );

  const onSelectListItem = useCallback(
    (rowData: AccountExtraitTransaction) => {
      if (rowData.transactionId) {
        onSelectTransaction(rowData.transactionId);
        navigate(
          getRoutePath({
            routeKey: 'account-transaction-details',
            customerId,
            accountId: selectedAccountId,
            transactionNo: rowData.transactionId,
          }),
        );
      }
    },
    [navigate, customerId, selectedAccountId, onSelectTransaction],
  );

  const config = useAccountExtraitTransactionsGridConfig(onToggleDrawer, onSelectListItem, onToggleFilterDrawer);

  const renderedTableGridActions = renderTableGridActions(config.actions);

  const formConfig = useAccountExtraitTransactionsFiltersConfig(filters, selectedAccountCurrency);

  const onDeleteChipFilter = (newFilters: Nullable<Partial<AccountExtraitTransactionListFilters>>) => {
    onChangeFilters(newFilters!);
  };

  const modifyDataDrawer = (newFilters: Partial<GenericObject>) => {
    if (newFilters.transactionDate) {
      newFilters.dateFrom = newFilters.transactionDate[0];
      newFilters.dateTo = newFilters.transactionDate[1];
    }
    if (!isDefined(newFilters.fromAmount)) {
      delete newFilters.fromAmount;
    }

    if (!isDefined(newFilters.toAmount)) {
      delete newFilters.toAmount;
    }

    if (newFilters?.transactionType === TRANSACTION_TYPE_DEFAULT_VALUE) {
      delete newFilters.transactionType;
    }

    delete newFilters.transactionDate;
    onChangeFilters(newFilters!);
  };

  const shouldShowTable = !transactionListError;
  const shouldShowTableActions = !isMobile && !isEmpty(config.actions);
  const totalPages = totalItems ? Math.ceil(totalItems / (filters?.pageSize ?? 1)) : undefined;
  const shouldShowPagination = isDefined(totalPages) && currentPage && onPageChange;

  return (
    <>
      <Box marginTop={4} width='100%' />
      <Box px={isMobile ? 3 : 0}>
        <CardHeading heading={t('accountExtraitTransactionsPage.CardHeading.Transactions')} hideOnMobile={false} />
      </Box>

      <Paper elevation={isMobile ? 0 : 1}>
        <Box display='flex' justifyContent='space-between' alignItems={'center'} paddingX={3} paddingY={2}>
          <Box>
            <SelectedFiltersChip filters={filters!} filtersConfig={formConfig} onChangeFilters={onDeleteChipFilter} />
          </Box>
          {shouldShowTableActions && <Box display={'flex'}>{renderedTableGridActions}</Box>}
        </Box>

        {transactionListError && (
          <Box px={3}>
            <ErrorAlert message={capitalizeFirstLetter(transactionListError.message)} />
          </Box>
        )}
        {shouldShowTable && (
          <TableGrid
            gridOptions={config.gridOptions}
            columnDefs={config.columnDefs}
            rowData={transactions}
            mobileListConfig={config.mobileListConfig}
            hasSkeleton={isLoadingTransactionList}>
            {shouldShowPagination && (
              <Pagination
                color='primary'
                count={totalPages}
                page={currentPage}
                siblingCount={isMobile ? 0 : undefined}
                onChange={onPageChange}
                disabled={false}
              />
            )}
          </TableGrid>
        )}
      </Paper>

      {!isMobile && isTransactionDetailsOpen && (
        <TransactionDetailsDrawer
          isOpen={isTransactionDetailsOpen}
          onToggleDrawer={onToggleDrawer}
          isLoading={isLoadingTransactionDetails || isLoadingCheckAuthorization}
          error={transactionDetailsError}
          transaction={selectedTransaction}
          transactionDetails={selectedTransactionDetails as ConsolidatedTransactionDetails}
          shouldFetchDealerRates={true}
          lastAuthorizer={checkAuthorization?.lastAuthorizer ?? false}
          needMandate={checkAuthorization?.needMandate ?? true}
        />
      )}
      <TableFilterDrawer
        formConfig={formConfig}
        isOpen={isTransactionsFilterOpen}
        onToggleDrawer={onToggleFilterDrawer}
        title={t('accountExtraitTransactionsPage.TransactionsFilterDrawer.Title')}
        applyButtonLabel={t('accountExtraitTransactionsPage.TransactionsFilterDrawer.BtnLabel')}
        onApplyDrawer={modifyDataDrawer}
        filters={{
          ...filters,
          transactionDate: [
            filters?.dateFrom ? new Date(filters.dateFrom) : null,
            filters?.dateTo ? new Date(filters.dateTo) : null,
          ],
          transactionType: filters?.transactionType ?? TRANSACTION_TYPE_DEFAULT_VALUE,
        }}
      />
    </>
  );
};

export { AccountExtraitTransactionsTable };
